import clsx from 'clsx';
import { FC, ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { QuickSaleUserModel } from '../models/QuickSaleModel';

type Condition = {
  key: string;
  icon: string;
  tone: string;
  extra?: ReactNode;
};

/** The basic commercial conditions, always in sight while selling. */
const CommercialConditions: FC<{ user: QuickSaleUserModel }> = ({ user }) => {
  const intl = useIntl();
  const { loyalty, mgm } = user;

  const conditions: Condition[] = [
    { key: 'noCommitment', icon: 'fa-crown', tone: 'primary' },
    { key: 'noPenalty', icon: 'fa-heart', tone: 'danger' },
    {
      key: 'referrals',
      icon: 'fa-users',
      tone: 'success',
      extra: mgm.code
        ? intl.formatMessage(
            { id: 'quickSale.referralCode' },
            { code: mgm.code, count: mgm.redemptions }
          )
        : null,
    },
    { key: 'vetAssistance', icon: 'fa-stethoscope', tone: 'info' },
    {
      key: 'gifts',
      icon: 'fa-gift',
      tone: 'warning',
      extra: intl.formatMessage(
        { id: 'quickSale.stamps' },
        {
          stamps: loyalty.stamps_required - loyalty.stamps_until_reward,
          required: loyalty.stamps_required,
        }
      ),
    },
  ];

  return (
    <div className="d-flex flex-wrap gap-4 mb-5">
      {conditions.map((condition) => (
        <div
          key={condition.key}
          className={clsx(
            'rounded p-5 text-center flex-grow-1 flex-basis-0 min-w-150px',
            `bg-light-${condition.tone}`
          )}
        >
          <i
            className={clsx(
              'fas fs-2x mb-3',
              condition.icon,
              `text-${condition.tone}`
            )}
          />
          <div
            className={clsx('fw-bolder fs-5 mb-1', `text-${condition.tone}`)}
          >
            {intl.formatMessage({ id: `quickSaleCondition.${condition.key}` })}
          </div>
          <div className="text-gray-700">
            {intl.formatMessage({
              id: `quickSaleCondition.${condition.key}Text`,
            })}
          </div>
          {condition.extra && (
            <div className="fw-bold text-gray-800 mt-2">{condition.extra}</div>
          )}
        </div>
      ))}
    </div>
  );
};

export { CommercialConditions };
